import { z } from 'zod';
import type { OnsiteLiveTeam, OnsiteLiveTeamWrite, TeamLimitWrite } from '@/types/oscar';

/**
 * Forms for Settings › Teams and limits. Widths and ranges match the server
 * (OnsiteLiveTeamRules.cs), which re-checks all of it.
 */

/** Team 15 Default (CatchAll): all limits 0; where an unmapped department falls. */
export const DEFAULT_TEAM_ID = 15;

/** Category 2 is Baseplan's PO limit, 1 its credit invoice limit (SQ06 job, 20:00). */
export const CATEGORY_PO = 2;
export const CATEGORY_CREDIT = 1;

const AMOUNT_MAX = 999_999_999_999;

/** A whole number of dollars, 0 or more, typed with or without commas and $. */
export const amountText = z
  .string()
  .trim()
  .min(1, 'Amount is required (0 = no authority)')
  .transform((v) => v.replace(/[$,\s]/g, ''))
  .refine((v) => /^\d+$/.test(v), 'A whole number of dollars, 0 or more')
  .refine((v) => Number(v) <= AMOUNT_MAX, 'Too large');

export function parseAmount(text: string): number | null {
  const r = amountText.safeParse(text);
  return r.success ? Number(r.data) : null;
}

// ── Team ──────────────────────────────────────────────────────────────────────

export const teamSchema = z.object({
  /** Only on create: the Baseplan team code, or an unused number for an OnsiteLive-only team. */
  TeamId: z.string().trim().regex(/^\d*$/, 'A whole number'),
  TeamCode: z.string().trim().min(1, 'Team name is required').max(50, 'At most 50 characters'),
  OLT: z.boolean(),
  IsActive: z.boolean(),
});
export type TeamFormData = z.infer<typeof teamSchema>;

export const emptyTeam: TeamFormData = { TeamId: '', TeamCode: '', OLT: false, IsActive: true };

export function teamToForm(t: OnsiteLiveTeam): TeamFormData {
  return { TeamId: String(t.TeamId), TeamCode: t.TeamCode, OLT: t.OLT, IsActive: t.IsActive };
}

export function toTeamPayload(d: TeamFormData, creating: boolean): OnsiteLiveTeamWrite {
  return {
    ...(creating ? { TeamId: Number(d.TeamId) } : {}),
    TeamCode: d.TeamCode.trim(),
    OLT: d.OLT,
    IsActive: d.IsActive,
  };
}

// ── Limit ─────────────────────────────────────────────────────────────────────

export const limitSchema = z.object({
  TeamId: z.string().min(1, 'Pick a team'),
  LineTypeId: z.string(),
  SubLineTypeId: z.string(),
  Amount: amountText,
});
export type LimitFormData = z.input<typeof limitSchema>;

export const emptyLimit: LimitFormData = { TeamId: '', LineTypeId: '', SubLineTypeId: '', Amount: '' };

export function toLimitPayload(d: z.output<typeof limitSchema>, categoryId: number): TeamLimitWrite {
  return {
    TeamId: Number(d.TeamId),
    CategoryId: categoryId,
    LineTypeId: d.LineTypeId === '' ? null : Number(d.LineTypeId),
    SubLineTypeId: d.SubLineTypeId === '' ? null : Number(d.SubLineTypeId),
    Amount: Number(d.Amount),
  };
}
